<template>
  <div class="app-chart-line-area chart-stacked">
    <div
      class="svg-wrapper pt-0"
      :class="{ 'has-y-axis': hasYAxis }"
    >
      <svg ref="svg" />
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { debounce } from 'lodash-es';

export default {
  name: 'AppChartLineArea',
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
    margin: {
      type: Object,
      default: () => ({
        top: 20,
        right: 0,
        bottom: 15,
        left: 0,
      }),
    },
    hasYAxis: {
      type: Boolean,
      default: true,
    },
    seriesColor: {
      type: String,
      default: '#7365ff',
    },
    yAxisWidth: {
      type: Number,
      default: 60,
    },
  },
  data: () => ({
    height: 320,
    width: 600,
  }),
  watch: {
    chartData: {
      handler() {
        this.drawChart();
      },
      deep: true,
    },
  },
  mounted() {
    const debouncedDrawChart = debounce(this.drawChart, 100);

    this.drawChart();

    this.resizeObserver = new ResizeObserver((entries) => {
      debouncedDrawChart();
    });

    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    drawChart() {
      const data = this.chartData.map((d) => ({ date: new Date(d.x + 'T12:00:00Z'), value: d.y }));
      const svgElement = this.$refs.svg;

      d3.select(svgElement).selectAll('*').remove();
      this.width = this.$el.clientWidth;

      const yMaxValue = d3.max(data, (d) => d.value);
      const yDomain = yMaxValue > 0 ? [0, yMaxValue] : [0, 10];
      const estYWidth = Math.round(yMaxValue).toString().length * 7 + 20;
      const width = this.width - this.margin.left - (this.margin.right + estYWidth);
      const height = this.height - this.margin.top - this.margin.bottom;
      const svg = d3
        .select(svgElement)
        .attr('width', this.width)
        .attr('height', this.height)
        .append('g')
        .attr('transform', `translate(${estYWidth + this.margin.left},${this.margin.top})`);
      const x = d3
        .scalePoint()
        .domain(data.map((d) => d.date))
        .range([12, width - 20]);
      const y = d3.scaleLinear().domain(yDomain).range([height, 0]).nice();
      const yAxis = d3.axisLeft(y).ticks(10).tickFormat(d3.format('d'));
      const yAxisGroup = svg
        .append('g')
        .attr('class', 'yAxis')
        .call(d3.axisLeft(y).tickSize(-width).tickFormat(''))
        .call(yAxis);

      yAxisGroup.selectAll('.tick line').attr('x2', width);
      yAxisGroup.select('.domain').remove();

      const xAxisGroup = svg
        .append('g')
        .attr('class', 'xAxis')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickSize(6).tickFormat(''));
      const labelValues = x.domain().filter((_d, i) => !(i % Math.ceil(data.length / 10)));

      xAxisGroup
        .selectAll('.tick')
        .filter((d) => labelValues.includes(d))
        .select('text')
        .text((d) => d3.timeFormat('%b %d')(d));

      svg
        .append('clipPath')
        .attr('id', 'chart-area')
        .append('rect')
        .attr('x', 0)
        .attr('y', -10)
        .attr('width', width)
        .attr('height', height + 10);

      const area = d3
        .area()
        .x((d) => x(d.date))
        .y0(height)
        .y1((d) => y(d.value));

      svg
        .append('path')
        .datum(data)
        .attr('clip-path', 'url(#chart-area)')
        .attr('fill', 'rgba(115, 101, 255, 0.2)')
        .attr('d', area);

      const line = d3
        .line()
        .x((d) => x(d.date))
        .y((d) => y(d.value));

      svg
        .append('path')
        .datum(data)
        .attr('clip-path', 'url(#chart-area)')
        .attr('fill', 'none')
        .attr('stroke', this.seriesColor)
        .attr('stroke-width', 1.5)
        .attr('d', line);

      svg
        .selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .attr('clip-path', 'url(#chart-area)')
        .attr('r', 3)
        .attr('cx', (d) => x(d.date))
        .attr('cy', (d) => y(d.value))
        .attr('fill', this.seriesColor)
        .attr('stroke', this.seriesColor);

      svg.selectAll('g.yAxis > g.tick').each(function () {
        const tick = d3.select(this);

        tick.append('line').attr('x1', 8).attr('stroke', '#777777');
      });

      svg.selectAll('g.xAxis > g.tick').each(function () {
        const tick = d3.select(this);

        tick.append('line').attr('y1', 6).attr('stroke', '#777777');
      });

      svg
        .selectAll('g.yAxis')
        .selectAll('.tick')
        .filter(function (_d, i, nodes) {
          return i === nodes.length - 1;
        })
        .select('line')
        .attr('stroke', '#f4f6f9');

      svg
        .selectAll('g.yAxis')
        .selectAll('.tick')
        .filter(function (_d, i, _nodes) {
          return i === 0;
        })
        .select('line')
        .attr('stroke', '#777777');

      const that = this;

      data.forEach((d, i) => {
        let barWidth = width / (data.length - 1);
        let barX = x(d.date) - barWidth / 2;

        if (data.length === 2) {
          barWidth = width / 2;
          barX = i * barWidth;
        } else {
          if (i === 0) {
            barWidth /= 2;
            barX = 0;
          } else if (i === data.length - 1) {
            barWidth /= 2;
            barX = x(d.date) - barWidth;
          }
        }

        svg
          .append('rect')
          .attr('x', barX)
          .attr('y', 0)
          .attr('width', barWidth)
          .attr('height', height)
          .attr('fill', 'none')
          .attr('pointer-events', 'all')
          .on('mouseover', function () {
            svg
              .selectAll('circle')
              .attr('r', 5)
              .attr('fill', '#FFF')
              .attr('stroke', that.seriesColor)
              .attr('stroke-width', 2);
          })
          .on('mouseout', function () {
            svg
              .selectAll('circle')
              .attr('r', 3)
              .attr('fill', that.seriesColor)
              .attr('stroke', that.seriesColor)
              .attr('stroke-width', 1);
          });
      });
    },
  },
};
</script>
